import AccordionPanel from 'components/ui/AccordionPanel'
import Loader from 'components/ui/Loader'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { classNames } from 'primereact/utils'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CampusService, { ICampusServiceExists } from 'service/CampusService'
import { ExemptionSpec, IEmployeeSummary } from 'types/Employee'
import { PlanTabs } from 'types/PlanTabs'
import AuthUtil from 'utils/AuthUtil'
import { OverlayPanel } from 'primereact/overlaypanel'
import { ISideBarModel } from 'types/CalendarType'

// assets
import commonOffice from 'assets/images/koc_ortak_ofis.svg'
import myOffice from 'assets/images/kendi_sirketim.svg'
import homeOffice from 'assets/images/koc_uzaktan.svg'
import doctorIcon from 'assets/icons/doctor-res-btn.svg'
import coiffeurIcon from 'assets/icons/coiffeur-res-btn.svg'
import gymIcon from 'assets/icons/gym-res-btn.svg'
import lactationIcon from 'assets/icons/lactation-res-btn.svg'
import meetingIcon from 'assets/icons/meeting-res-btn.svg'
import poolVehicleIcon from 'assets/icons/pool-vehicle-res-btn.svg'
import warningIcon from 'assets/icons/warning-icon.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import { uniqByProp } from 'utils/Miscellaneous'
import MultiSidebarInfo from './MultiSidebarInfo'

interface ComponentProps {
  hasEvent: boolean
  model: ISideBarModel[]
  setDeleteDialog: Dispatch<SetStateAction<boolean>>
  myPlanList: () => void
  setPlanTypeItems: Dispatch<SetStateAction<any[]>>
  onClickOfficePlan: (arg0: any) => any
  onClickCommonPlan: (arg0: any) => any
  onClickRemotePlan: (arg0: any) => any
  onClickHealthPlan: (arg0: any) => any
  onClickCoiffeurPlan: (arg0: any) => any
  onClickGymPlan: (arg0: any) => any
  onClickLactationPlan: (arg0: any) => any
  onClickMeetingPlan: (arg0: any) => any
  onClickPoolVehiclePlan: (arg0: any) => any
  onClickRedirectOfficePlan: (arg0: any) => any
  refreshCalendar: () => void
  getAlertRedirect: (value: any) => void
}

export default function MultiSidebarContent(props: ComponentProps) {
  const { hasEvent, model, setDeleteDialog, myPlanList, setPlanTypeItems, refreshCalendar, getAlertRedirect } = props

  const { t } = useTranslation()
  const homeOfficeText = t('tabs.remote-work')
  const commonOfficeText = t('most-used-words.common-working')
  const officeText = t('tabs.company-work')
  const healthText = t('tabs.campus-health')
  const coiffeurText = t('tabs.campus-coiffeur')
  const lactationRoomText = t('tabs.campus-lactationRoom')
  const meetingRoomText = t('tabs.campus-meetingRoom')
  const poolVehicleText = t('tabs.campus-poolVehicle')
  const gymText = t('tabs.campus-gym')
  const employee = AuthUtil.getEmployeeSummary() as IEmployeeSummary

  const [planTabs, setPlanTabs] = useState<PlanTabs[]>([])
  /* Has Campus Service */
  const [campusVisible, setCampusVisible] = useState<ICampusServiceExists | undefined>()
  const [campusVisibleLoading, setCampusVisibleLoading] = useState(false)
  const [hasCampusPlan, setHasCampusPlan] = useState<boolean>(false)
  const {
    isAnyExist,
    isCoiffeurServiceExist,
    isGymServiceExist,
    isHealthServiceExist,
    isLactationServiceExist,
    isMeetingServiceExist,
    isParkAreaServiceExist,
    isPoolVehicleServiceExist,
  } = campusVisible || {}
  const hasHealth = AuthUtil.hasHealth(employee?.exemptionSpecList!)
  const hasGym = AuthUtil.hasGym(employee?.exemptionSpecList!)
  const hasCoiffeur = AuthUtil.hasCoiffeur(employee?.exemptionSpecList!)
  const hasLactation = AuthUtil.hasLactation(employee?.exemptionSpecList!)
  const hasMeeting = AuthUtil.hasMeeting(employee?.exemptionSpecList!)
  const hasPoolVehicle = AuthUtil.hasPoolVehicle(employee?.exemptionSpecList!)
  const hasNoPlan = planTabs.length === 0 && !hasCampusPlan

  const allPlanTypeItems = [
    { label: t('plantype.campus-health'), key: 101 },
    { label: t('plantype.campus-coiffeur'), key: 102 },
    { label: t('plantype.campus-gym-salon'), key: 103 },
    { label: t('plantype.campus-gym-group-lesson'), key: 104 },
    { label: t('plantype.campus-gym-trainer'), key: 105 },
    { label: t('plantype.campus-lactationRoom'), key: 106 },
    { label: t('plantype.campus-meetingRoom'), key: 108 },
    { label: t('plantype.campus-poolVehicle'), key: 111 },
    { label: t('plantype.work-from-remote-label'), key: 1 },
    { label: t('plantype.work-from-office-label'), key: 2 },
    { label: t('plantype.work-from-office-label'), key: 5 },
    { label: t('plantype.work-from-shared-label'), key: 4 },
    { label: t('plantype.work-from-shared-label'), key: 6 },
  ]
  const uniqueById = uniqByProp('key')
  const getSelectedPlanTypes = () => {
    let dayList = props.model.filter((item) => item.day.isChecked)
    dayList.sort((day1, day2) => day1.day.date - day2.day.date)
    const filtered: any[] = []
    dayList.forEach((value) => {
      filtered.push(allPlanTypeItems.find((x) => x.key === value.event.planType))
      value.event?.doctorReservation?.forEach((f: any) => {
        filtered.push(allPlanTypeItems.find((x) => x.key === f.planType))
      })
      value.event?.coiffeurReservation?.forEach((f: any) => {
        filtered.push(allPlanTypeItems.find((x) => x.key === f.planType))
      })
      value.event?.gymSaloonReservation?.forEach((f: any) => {
        filtered.push(allPlanTypeItems.find((x) => x.key === f.planType))
      })
      value.event?.gymGroupLessonReservation?.forEach((f: any) => {
        filtered.push(allPlanTypeItems.find((x) => x.key === f.planType))
      })
      value.event?.gymTrainerReservation?.forEach((f: any) => {
        filtered.push(allPlanTypeItems.find((x) => x.key === f.planType))
      })
      value.event?.lactationReservation?.forEach((f: any) => {
        filtered.push(allPlanTypeItems.find((x) => x.key === f.planType))
      })
      value.event?.meetingRoomReservation?.forEach((f: any) => {
        filtered.push(allPlanTypeItems.find((x) => x.key === f.planType))
      })
      value.event?.poolVehicleReservation?.forEach((f: any) => {
        filtered.push(allPlanTypeItems.find((x) => x.key === f.planType))
      })
    })
    const unifiedArray = uniqueById(filtered)
    setPlanTypeItems(unifiedArray)
  }

  useEffect(() => {
    getSelectedPlanTypes()
    setHasCampusPlan(AuthUtil.hasCampus(employee.exemptionSpecList))
    setPlanTabs(AuthUtil.getPlanTabsAuthorized(employee.exemptionSpecList))
    setCampusVisibleLoading(true)
    CampusService.getCampusServiceExists()
      .then((r) => setCampusVisible(r.data))
      .catch((err) => console.log(err))
      .finally(() => setCampusVisibleLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* On Campus button Disabled state */
  const dayList = model.filter((x) => x.day.isChecked)
  const onCampusDisabled = dayList.length > 1
  const opCampusDisabledRef = useRef<OverlayPanel>(null)
  const hasLeaveOrMedical = dayList.find((x) => {
    if (!!x.event.leave?.length || !!x.event.medicalCertificate?.length) return true
    else return false
  })
  const hasOnlyLeaveOrMedical = dayList.every((x) => {
    if (!!x.event.leave?.length || !!x.event.medicalCertificate?.length) return true
    else return false
  })

  const onCampusDisabledInfoPanel = () => {
    return (
      <>
        <div
          className="d-flex align-items-center"
          onMouseOver={(e) => opCampusDisabledRef.current?.show(e, e.target)}
          onMouseLeave={(e) => opCampusDisabledRef.current?.hide()}>
          <i className="pi pi-info-circle ms-2" style={{ color: '#3D8499', fontSize: '1.4rem' }}></i>
        </div>
        <OverlayPanel
          dismissable
          ref={opCampusDisabledRef}
          id="overlay_panel_disabled_campus"
          style={{ width: '300px', borderRadius: '10px', fontSize: '14px' }}>
          {t('overlaypanel-texts.on-campus-selection')}
        </OverlayPanel>
      </>
    )
  }

  const shouldRenderComponent =
    !!campusVisible &&
    isAnyExist &&
    (hasCampusPlan || isParkAreaServiceExist || isMeetingServiceExist || isPoolVehicleServiceExist)

  /* Collapsible Panel */
  const [collapsed, setCollapsed] = useState(!hasEvent)
  const panelTitle = collapsed ? (
    <div className="d-flex gap-2">
      <>
        {shouldRenderComponent ? (
          <>
            {!!planTabs.length && !hasLeaveOrMedical ? (
              <>
                <span className="fw-bold">{t('sidebar.work-notify-title')}</span>
                <strong>{t('most-used-words.or')}</strong>
              </>
            ) : null}
            <span className="fw-bold">{t('sidebar.campus-notify')}</span>
          </>
        ) : (
          <>{!hasLeaveOrMedical && <span className="fw-bold">{t('sidebar.work-notify')}</span>}</>
        )}
      </>
    </div>
  ) : (
    <>{!!planTabs.length && !hasLeaveOrMedical ? <span className="fw-bold">{t('sidebar.work-notify')}</span> : null}</>
  )

  return (
    <>
      <MultiSidebarInfo
        model={props.model}
        refreshCalendar={refreshCalendar}
        getAlertRedirect={getAlertRedirect}
        isParkAreaServiceExist={isParkAreaServiceExist}
      />

      {hasNoPlan ? (
        <div>
          <div className="w-100 h-100 d-flex flex-column gap-3 justify-content-center align-items-center p-3">
            <img src={warningIcon} width={65} height={65} alt="no-reservation" />
            <span className="fw-semibold text-center">{t('homepage.no-plan-tabs')}</span>
          </div>
        </div>
      ) : (
        <AccordionPanel
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          title={panelTitle}
          headerContent={
            <>
              {!hasLeaveOrMedical ? (
                <Divider className="mb-2 mt-0" layout="vertical">
                  <strong>{t('most-used-words.or')}</strong>
                </Divider>
              ) : (
                <Divider className="mt-2 mb-2"></Divider>
              )}

              {!hasOnlyLeaveOrMedical && (
                <div className="of-multiple-plan-bottom-buttons pb-2 px-3">
                  <Button
                    style={{ width: '100%' }}
                    id="cancelNotification"
                    label={t('buttons.cancel-chosen-days')}
                    disabled={hasEvent}
                    onClick={() => setDeleteDialog(true)}
                  />
                </div>
              )}
            </>
          }>
          <div className="of-multiple-plan-bottom-buttons">
            {/* Work Notify */}

            {!hasLeaveOrMedical ? (
              <div className="d-flex">
                <div
                  id="companyOfficePlan"
                  className="m-1 of-plan-create-box of-bordered-box of-office-border  bg-officeplan of-clickable"
                  onClick={() => props?.onClickOfficePlan(model)}
                  style={
                    employee.exemptionSpecList?.filter((x: ExemptionSpec) => x.code === 'OFFICE').length > 0 ? { display: 'none' } : {}
                  }>
                  <div className="of-white-panel">
                    <img className="of-new-plan-icon" src={myOffice} alt="" />
                    <div className="d-flex of-plan-card-direction">
                      <p>{officeText}</p>
                      <i className="arrow pi pi-angle-right" onClick={() => props?.onClickOfficePlan(model)} />
                    </div>
                  </div>
                </div>
                <div
                  id="commonOfficePlan"
                  className="m-1 of-plan-create-box of-bordered-box of-common-border  bg-commonplan of-clickable"
                  onClick={() => props?.onClickCommonPlan(model)}
                  style={
                    employee.exemptionSpecList?.filter((x: ExemptionSpec) => x.code === 'COMMON').length > 0 ? { display: 'none' } : {}
                  }>
                  <div className="of-white-panel">
                    <img className="of-new-plan-icon" src={commonOffice} alt="" />
                    <div className="d-flex of-plan-card-direction ">
                      <p>{commonOfficeText}</p>
                      <i className="arrow pi pi-angle-right" onClick={() => props?.onClickCommonPlan(model)} />
                    </div>
                  </div>
                </div>
                <div
                  id="remotePlan"
                  className="m-1 of-plan-create-box of-bordered-box of-home-border  bg-homeplan of-clickable"
                  onClick={() => myPlanList()}
                  style={
                    employee.exemptionSpecList?.filter((x: ExemptionSpec) => x.code === 'REMOTE').length > 0 ? { display: 'none' } : {}
                  }>
                  <div className="of-white-panel">
                    <img className="of-new-plan-icon" src={homeOffice} alt="" />
                    <div className="d-flex of-plan-card-direction">
                      <p>{homeOfficeText}</p>
                      <i className="arrow pi pi-angle-right" onClick={() => myPlanList()} />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Bottom Cards */}
            {campusVisibleLoading ? (
              <Loader src={calendarLoading} />
            ) : (
              <>
                {shouldRenderComponent ? (
                  <>
                    <div className={classNames('d-flex align-items-center', hasLeaveOrMedical ? 'mb-3 mt-0' : 'my-2')}>
                      <strong>{t('sidebar.campus-notify')}</strong>
                      {onCampusDisabled ? onCampusDisabledInfoPanel() : null}
                    </div>

                    {/* On Campus Notify */}
                    <div className="d-flex flex-wrap">
                      {/* Doctor */}
                      {hasHealth && isHealthServiceExist && !hasLeaveOrMedical && (
                        <div
                          id="doctorPlan"
                          className={classNames(
                            'm-1 of-plan-create-box of-bordered-box of-campus-border bg-campusplan of-clickable',
                            onCampusDisabled ? 'of-campus-disabled' : ''
                          )}
                          onClick={() => props?.onClickHealthPlan(model)}
                          style={onCampusDisabled ? { pointerEvents: 'none' } : {}}>
                          <div className="of-white-panel ">
                            <img className="of-campus-plan-icon flex-grow-1" src={doctorIcon} alt="doctor" />
                            <div className="d-flex of-plan-card-direction">
                              <p>{healthText}</p>
                              <i className="arrow pi pi-angle-right" onClick={() => props?.onClickHealthPlan(model)} />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Gym */}
                      {hasGym && isGymServiceExist && (
                        <div
                          id="gymPlan"
                          className={classNames(
                            'm-1 of-plan-create-box of-bordered-box of-campus-border bg-campusplan of-clickable',
                            onCampusDisabled ? 'of-campus-disabled' : ''
                          )}
                          onClick={() => props?.onClickGymPlan(model)}
                          style={onCampusDisabled ? { pointerEvents: 'none' } : {}}>
                          <div className="of-white-panel">
                            <img className="of-campus-plan-icon" src={gymIcon} alt="gym" />
                            <div className="d-flex of-plan-card-direction">
                              <p>{gymText}</p>
                              <i className="arrow pi pi-angle-right" />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Coiffeur */}
                      {hasCoiffeur && isCoiffeurServiceExist && (
                        <div
                          id="coiffeurPlan"
                          className={classNames(
                            'm-1 of-plan-create-box of-bordered-box of-campus-border bg-campusplan of-clickable',
                            onCampusDisabled ? 'of-campus-disabled' : ''
                          )}
                          onClick={() => props?.onClickCoiffeurPlan(model)}
                          style={onCampusDisabled ? { pointerEvents: 'none' } : {}}>
                          <div className="of-white-panel">
                            <img className="of-campus-plan-icon" src={coiffeurIcon} alt="coiffeur" />
                            <div className="d-flex of-plan-card-direction">
                              <p>{coiffeurText}</p>
                              <i className="arrow pi pi-angle-right" />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Lactation Room */}
                      {hasLactation && isLactationServiceExist && !hasLeaveOrMedical && (
                        <div
                          id="lactationRoomPlan"
                          className={classNames(
                            'm-1 of-plan-create-box of-bordered-box of-campus-border bg-campusplan of-clickable',
                            onCampusDisabled ? 'of-campus-disabled' : ''
                          )}
                          onClick={() => props?.onClickLactationPlan(model)}
                          style={onCampusDisabled ? { pointerEvents: 'none' } : {}}>
                          <div className="of-white-panel">
                            <img className="of-campus-plan-icon" src={lactationIcon} alt="lactation" />
                            <div className="d-flex of-plan-card-direction">
                              <p>{lactationRoomText}</p>
                              <i className="arrow pi pi-angle-right" />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Meeting Room */}
                      {hasMeeting && isMeetingServiceExist && !hasLeaveOrMedical && (
                        <div
                          id="meetingRoomPlan"
                          className={classNames(
                            'm-1 of-plan-create-box of-bordered-box of-campus-border bg-campusplan of-clickable',
                            onCampusDisabled ? 'of-campus-disabled' : ''
                          )}
                          onClick={() => props?.onClickMeetingPlan(model)}
                          style={onCampusDisabled ? { pointerEvents: 'none' } : {}}>
                          <div className="of-white-panel">
                            <img className="of-campus-plan-icon" src={meetingIcon} alt="meeting-room-res" />

                            <div className="d-flex of-plan-card-direction">
                              <p>{meetingRoomText}</p>
                              <i className="arrow pi pi-angle-right" />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pool Vehicle */}
                      {hasPoolVehicle && isPoolVehicleServiceExist && (
                        <div
                          id="poolVehiclePlan"
                          className={classNames(
                            'm-1 of-plan-create-box of-bordered-box of-campus-border bg-campusplan of-clickable',
                            onCampusDisabled ? 'of-campus-disabled' : ''
                          )}
                          onClick={() => props?.onClickPoolVehiclePlan(model)}
                          style={onCampusDisabled ? { pointerEvents: 'none' } : {}}>
                          <div className="of-white-panel">
                            <img className="of-campus-plan-icon" src={poolVehicleIcon} alt="pool-vehicle-res" />

                            <div className="d-flex of-plan-card-direction">
                              <p>{poolVehicleText}</p>
                              <i className="arrow pi pi-angle-right" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </>
            )}
            {!hasOnlyLeaveOrMedical && (
              <>
                <div className="my-2 w-100 d-flex justify-content-center">
                  <strong>{t('most-used-words.or')}</strong>
                </div>

                {/* Button */}
                <div className="row">
                  <div className="col-md-12">
                    <Button
                      id="cancelNotification"
                      label={t('buttons.cancel-chosen-days')}
                      disabled={hasEvent}
                      onClick={() => setDeleteDialog(true)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </AccordionPanel>
      )}
    </>
  )
}
